import type * as CSS from 'csstype'
import tinycolor from 'tinycolor2'

export const BLACK = '#1B1918'
export const WHITE = '#FFFFFF'

type Shade = typeof BLACK | typeof WHITE | `var(${string})`

/**
 * The purpose of this method is to select either black or white as a
 * contrasting color. For example, given a background color, verify the text has
 * a high enough contrast to be legible.
 *
 * ! Important! Any style prop can be a color value or "var(--theme-*)".
 *
 * When calculating the contrast color:
 * - Color is a theme variable:
 *    - Return the associated theme contrast variable.
 * - Color is a valid color (per tinycolor2):
 *    - Calculate and return the contrast color (as RGB).
 * - Color is invalid:
 *    - Return black (for historical reasons)
 *    - Or, return fallback theme contrast variable if provided.
 *
 * Lastly, this function has only been tested with HEX and RGB(A) colors. Other
 * color formats, like HSL, should work though.
 */
export const selectContrastingColor = (
  cssColor: CSS.Property.Color | undefined,
  fallbackCssVar?: string,
): Shade => {
  // * Assumes variable is a theme color, and the fallback variable exists.
  if (cssColor && (cssColor.startsWith('var') || cssColor.startsWith('--'))) {
    return `var(${fallbackCssVar})`
  }

  const color = tinycolor(cssColor)

  if (!color.isValid()) {
    if (fallbackCssVar) {
      return `var(${fallbackCssVar})`
    }

    return BLACK
  }

  const { r, g, b } = color.toRgb()

  /*
   * Break up hex code into R G B intensity values
   * Determine the luminance value for RGB color
   * Return black or white depending on luminance value
   *
   * https://stackoverflow.com/a/41491220
   *
   * https://www.w3.org/TR/WCAG20/#relativeluminancedef
   */

  const contrastColorNormalized = [r / 255, g / 255, b / 255]

  const luminanceArr = contrastColorNormalized.map((contrastCol) => {
    if (contrastCol <= 0.03928) {
      return contrastCol / 12.92
    }

    return ((contrastCol + 0.055) / 1.055) ** 2.4
  })

  const contrastColorLuminance =
    0.2126 * luminanceArr[0] + 0.7152 * luminanceArr[1] + 0.0722 * luminanceArr[2]

  return contrastColorLuminance > 0.179 ? BLACK : WHITE
}

/**
 * Creates a very light tint of the given color, optimized for background use
 * @param color - The base color to lighten
 * @returns A very light version of the color, with safeguard against pure white
 */
export const createLightTint = (color: string): string => {
  const tinyColor = tinycolor(color)

  if (!tinyColor.isValid()) {
    // Fallback to a light gray if color is invalid
    return '#f5f5f5'
  }

  // Create a very light tint by lightening the color significantly
  let lightTint = tinyColor.lighten(55)

  // Safeguard: if the result is white or very close to white, make it slightly darker
  const hsl = lightTint.toHsl()

  if (hsl.l >= 0.98) {
    // Very close to white (98% lightness or higher)
    lightTint = tinyColor.lighten(50) // Use less lightening
  }

  return lightTint.toString()
}
